import { useEffect, useState, useRef } from 'react';

// Global scroll animation system with improved stability
export const useGlobalScrollAnimation = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const [scrollDirection, setScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset;
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      setScrollDirection(direction);
      setLastScrollY(currentScrollY);

      const sections = document.querySelectorAll('section[id]');
      const newAnimatedSections = new Set();

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.3;
        
        if (isInView) {
          newAnimatedSections.add(section.id);
          
          // Always animate in when section is in view, regardless of direction
          const progressiveElements = section.querySelectorAll('.progressive-element');
          progressiveElements.forEach((element, index) => {
            // Clear any existing timeouts
            if (element.animationTimeout) {
              clearTimeout(element.animationTimeout);
            }
            
            element.animationTimeout = setTimeout(() => {
              element.classList.add('loaded');
              element.classList.remove('unloaded');
            }, index * 100);
          });
          
          const dynamicElements = section.querySelectorAll('.dynamic-content');
          dynamicElements.forEach((element) => {
            element.classList.add('loaded');
            element.classList.remove('unloaded');
          });
        } else {
          // Only animate out if scrolling up AND section is completely out of view
          if (direction === 'up' && (rect.bottom < 0 || rect.top > window.innerHeight)) {
            const progressiveElements = section.querySelectorAll('.progressive-element');
            progressiveElements.forEach((element) => {
              element.classList.remove('loaded');
              element.classList.add('unloaded');
            });
            
            const dynamicElements = section.querySelectorAll('.dynamic-content');
            dynamicElements.forEach((element) => {
              element.classList.remove('loaded');
              element.classList.add('unloaded');
            });
          }
        }
      });

      setAnimatedSections(newAnimatedSections);
    };

    // Initial check
    handleScroll();
    
    // Add scroll listener with throttling for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [lastScrollY]);

  return { animatedSections, scrollDirection };
};

// Hook for individual elements
export const useElementScrollAnimation = (delay = 0) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
            if (elementRef.current) {
              elementRef.current.classList.add('loaded');
            }
          }, delay);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px 0px -50px 0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [delay]);

  return { isVisible, elementRef };
};
